// screens/create-product-mgt/AllocateIngredients.jsx
import React, { useState, useEffect } from 'react';
import { Form, Row, Col, Button, Card, Alert, Spinner, Badge, Modal } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axios';
// ─── FIXES IN THIS FILE ──────────────────────────────────────────────────────
//
// FIX A: Compact ingredient rows — each ingredient is now a slim horizontal
//         row (select + qty + unit + flags) inside a clean table-like list.
//         Addon/conditional fields expand inline on demand via an "Advanced"
//         toggle per row. This eliminates the wall of nested grey cards.
//
// FIX B: Removed the post-save "Complete Product Creation?" confirmation modal.
//         After a successful save, we show an inline success banner with a
//         single "Create another product" button. No double confirmation.
//
// FIX C: Button hierarchy in footer:
//         LEFT:   ← Back                  (outline-secondary)
//         CENTER: Skip ingredients         (link/muted — de-emphasised)
//         RIGHT:  [Save Ingredients]       (success — primary CTA)
//                 or [Update Ingredients]  (primary)
//
// ─────────────────────────────────────────────────────────────────────────────

const AllocateIngredients = ({
  data, onNext, onBack, updateData, currentStep,
  isSubmitting, setIsSubmitting, setErrors, onComplete
}) => {
  const accessToken = localStorage.getItem("authToken");

  const [isLoading, setIsLoading]           = useState(false);
  const [isSaving, setIsSaving]             = useState(false);
  const [isUpdating, setIsUpdating]         = useState(false);
  const [categoryList, setCategoryList]     = useState([]);
  const [addons, setAddons]                 = useState([]);
  const [rawMaterialList, setRawMaterialList] = useState([]);
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);
  const [existingIngredients, setExistingIngredients] = useState(null);

  // FIX B: inline success state instead of a modal
  const [saveSuccess, setSaveSuccess] = useState(false);

  // FIX A: track which rows have their advanced panel open
  const [expandedRows, setExpandedRows] = useState({});

  const getDefaultConditionalAddon = () => ({ addonId: '', addonValueId: '', quantity: '' });
  const getDefaultRawMaterial = () => ({
    rawMaterial: '', quantity: '', unit: '',
    addonId: '', addonValueId: '',
    isImportant: false, isOptional: false,
    conditionalAddons: []
  });

  const [formData, setFormData] = useState({
    product: '',
    rawMaterials: [getDefaultRawMaterial()]
  });

  const productData = data.product || {};
  const productId   = productData._id;

  useEffect(() => {
    getCategoryList();
    getAddons();
    getRawMaterials();
    if (productId) {
      setFormData(prev => ({ ...prev, product: productId }));
      fetchExistingIngredients();
    }
  }, [productId]);

  const getCategoryList = async () => {
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      const response = await axiosInstance.get("masters/category/get", { headers });
      if (response?.data?.status === true) setCategoryList(response.data.data.categories);
    } catch { /* non-critical */ }
  };

  const getAddons = async () => {
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      const response = await axiosInstance.get("addons?page=1&limit=1000", { headers });
      setAddons(response?.data?.data?.addons || []);
    } catch { /* non-critical */ }
  };

  const getRawMaterials = async () => {
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      const response = await axiosInstance.get("raw-material", { headers });
      if (response?.data?.status === true) {
        const materials = (response.data.data.rawMaterials || []).map((m, i) => ({ ...m, serialNumber: i + 1 }));
        setRawMaterialList(materials);
      }
    } catch {
      toast.error("Failed to load raw materials", { position: "top-right", autoClose: 3000 });
    }
  };

  const fetchExistingIngredients = async () => {
    if (!productId) return;
    try {
      setIsLoading(true);
      const headers = { Authorization: `Bearer ${accessToken}` };
      const response = await axiosInstance.get(`recipe?product=${productId}`, { headers });
      if (response?.data?.status === true && response.data.data.recieps?.length > 0) {
        const recipe = response.data.data.recieps[0];
        setExistingIngredients(recipe);
        const mapped = recipe.rawMaterials?.map(item => ({
          _id: item?._id,
          rawMaterial: item?.rawMaterial?._id || '',
          quantity: item?.quantity || '',
          unit: item?.rawMaterial?.unit || '',
          addonId: item?.addonId?._id || '',
          addonValueId: item?.addonValueId?._id || item?.addonValueId || '',
          isImportant: item?.isImportant || false,
          isOptional: item?.isOptional || false,
          conditionalAddons: (item?.conditionalAddons || []).map(c => ({
            addonId: c?.addonId?._id || c?.addonId || '',
            addonValueId: c?.addonValueId?._id || c?.addonValueId || '',
            quantity: c?.quantity || ''
          }))
        })) || [getDefaultRawMaterial()];
        setFormData({ _id: recipe?._id, product: productId, rawMaterials: mapped.length ? mapped : [getDefaultRawMaterial()] });
        toast.info('Existing ingredients loaded.', { position: "top-right", autoClose: 2000 });
      }
    } catch { /* non-critical */ }
    finally { setIsLoading(false); }
  };

  const handleRawMaterialChange = (index, field, value) => {
    const updated = [...formData.rawMaterials];
    updated[index] = { ...updated[index], [field]: value };
    if (field === 'rawMaterial') {
      const mat = rawMaterialList.find(rm => rm._id === value);
      updated[index].unit = mat?.unit || '';
    }
    if (field === 'addonId') {
      updated[index].addonValueId = '';
      updated[index].conditionalAddons = [];
    }
    setFormData(prev => ({ ...prev, rawMaterials: updated }));
  };

  const addRawMaterialEntry = () => {
    setFormData(prev => ({ ...prev, rawMaterials: [...prev.rawMaterials, getDefaultRawMaterial()] }));
  };

  const removeRawMaterialEntry = (index) => {
    setFormData(prev => {
      const next = prev.rawMaterials.filter((_, i) => i !== index);
      return { ...prev, rawMaterials: next.length ? next : [getDefaultRawMaterial()] };
    });
    setExpandedRows(prev => {
      const copy = { ...prev };
      delete copy[index];
      return copy;
    });
  };

  const toggleRowExpand = (index) => {
    setExpandedRows(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const addConditionalAddon = (rmIdx) => {
    setFormData(prev => {
      const updated = [...prev.rawMaterials];
      updated[rmIdx].conditionalAddons = [...(updated[rmIdx].conditionalAddons || []), getDefaultConditionalAddon()];
      return { ...prev, rawMaterials: updated };
    });
  };

  const removeConditionalAddon = (rmIdx, condIdx) => {
    setFormData(prev => {
      const updated = [...prev.rawMaterials];
      updated[rmIdx].conditionalAddons = updated[rmIdx].conditionalAddons.filter((_, i) => i !== condIdx);
      return { ...prev, rawMaterials: updated };
    });
  };

  const handleConditionalAddonChange = (rmIdx, condIdx, field, value) => {
    setFormData(prev => {
      const updated = [...prev.rawMaterials];
      const conds   = [...updated[rmIdx].conditionalAddons];
      conds[condIdx] = { ...conds[condIdx], [field]: value };
      if (field === 'addonId') conds[condIdx].addonValueId = '';
      updated[rmIdx].conditionalAddons = conds;
      return { ...prev, rawMaterials: updated };
    });
  };

  const validateForm = () => {
    if (!productId) {
      toast.error('Product not created yet. Go back to Step 1.', { position: 'top-right' });
      return false;
    }
    const hasValid = formData.rawMaterials.some(rm => rm.rawMaterial);
    if (!hasValid) {
      toast.error('Add at least one raw material, or skip this step.', { position: 'top-right' });
      return false;
    }
    for (let i = 0; i < formData.rawMaterials.length; i++) {
      const rm = formData.rawMaterials[i];
      if (!rm.rawMaterial && !rm.quantity) continue;
      if (!rm.rawMaterial) { toast.error(`Select a raw material for entry ${i + 1}`, { position: 'top-right' }); return false; }
      if (!rm.quantity || parseFloat(rm.quantity) <= 0) { toast.error(`Enter a valid quantity for entry ${i + 1}`, { position: 'top-right' }); return false; }
      if ((rm.addonId && !rm.addonValueId) || (!rm.addonId && rm.addonValueId)) {
        toast.error(`Select both addon group and value for entry ${i + 1}`, { position: 'top-right' }); return false;
      }
      for (let j = 0; j < (rm.conditionalAddons || []).length; j++) {
        const c = rm.conditionalAddons[j];
        if (!c.addonId || !c.addonValueId || !c.quantity || parseFloat(c.quantity) <= 0) {
          toast.error(`Complete conditional addon #${j + 1} for entry ${i + 1}`, { position: 'top-right' }); return false;
        }
      }
    }
    return true;
  };

  const confirmSkip = async () => {
    setShowSkipConfirm(false);
    if (!productId) { toast.error('Product not created yet.', { position: 'top-right' }); return; }
    setIsSaving(true); setIsSubmitting(true);
    try {
      updateData('ingredients', { items: [], payload: null, totalIngredients: 0, isAllocated: false, skipped: true });
      toast.success('Ingredients step skipped.', { position: "top-right", autoClose: 2000 });
      if (onComplete) onComplete();
    } finally { setIsSaving(false); setIsSubmitting(false); }
  };

  const handleSaveOrUpdate = async () => {
    if (!validateForm()) return;
    const valid = formData.rawMaterials.filter(rm => rm.rawMaterial && rm.quantity && parseFloat(rm.quantity) > 0);
    if (!valid.length) { toast.error('Add at least one valid raw material.', { position: 'top-right' }); return; }

    const clean = (v) => (v?.toString().trim() === "" ? null : v);
    const payload = {
      product: productId,
      rawMaterials: valid.map(rm => ({
        rawMaterial: rm.rawMaterial,
        quantity: Number(rm.quantity),
        unit: rm.unit,
        addonId: clean(rm.addonId),
        addonValueId: clean(rm.addonValueId),
        isImportant: rm.isImportant || false,
        isOptional: rm.isOptional || false,
        conditionalAddons: (rm.conditionalAddons || []).map(c => ({
          addonId: clean(c.addonId),
          addonValueId: clean(c.addonValueId),
          quantity: Number(c.quantity)
        }))
      }))
    };
    if (formData._id) payload._id = formData._id;

    setIsUpdating(true); setIsSubmitting(true);
    try {
      const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` };
      const method   = payload._id ? 'put' : 'post';
      const response = await axiosInstance[method]("recipe", payload, { headers });

      if (response?.data?.status === true) {
        const msg = payload._id ? 'Ingredients updated!' : 'Ingredients saved!';
        toast.success(msg, { position: "top-right", autoClose: 2000 });
        updateData('ingredients', {
          items: valid, payload, totalIngredients: valid.length,
          isAllocated: true, skipped: false,
          recipeId: response.data.data?.dbResponse?._id || formData._id
        });
        // FIX B: show inline success banner — no second modal
        setSaveSuccess(true);
      } else {
        toast.error(response?.data?.error || 'Failed to save ingredients', { position: "top-right" });
      }
    } catch (error) {
      let msg = 'Failed to save ingredients';
      if (error.response) {
        const d = error.response.data;
        msg = d?.error?.message || d?.data?.message || d?.error || msg;
      }
      toast.error(msg, { position: "top-right", autoClose: 3000 });
      setErrors([msg]);
    } finally { setIsUpdating(false); setIsSubmitting(false); }
  };

  const rawMaterialOptions = rawMaterialList.map(rm => ({
    value: rm._id,
    label: `${rm.name} (${rm.unit})${rm.inStock === false ? " — Out of stock" : ""}`
  }));

  const addonOptions = addons.map(a => ({
    value: a._id,
    label: `${a.title} ${a.status === "ACTIVE" ? "✅" : "⏸️"}`
  }));

  const getAddonValueOptions = (addonId) => {
    if (!addonId) return [];
    const addon = addons.find(a => a._id === addonId);
    return addon?.addonValues?.map(v => ({ value: v._id, label: v.value })) || [];
  };

  const totalValid = formData.rawMaterials.filter(rm => rm.rawMaterial && rm.quantity && parseFloat(rm.quantity) > 0).length;

  // ─── FIX B: Inline success state ─────────────────────────────────────────
  if (saveSuccess) {
    return (
      <div className="allocate-ingredients-step">
        <div style={{
          textAlign: 'center', padding: '3rem 2rem',
          background: '#f0fdf4', borderRadius: 12,
          border: '1px solid #bbf7d0'
        }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
          <h5 style={{ color: '#15803d', marginBottom: 8 }}>
            Ingredients {formData._id ? 'updated' : 'saved'}!
          </h5>
          <p style={{ color: '#4b7a59', fontSize: 14, marginBottom: 24 }}>
            {totalValid} ingredient{totalValid !== 1 ? 's' : ''} saved for <strong>{productData.name}</strong>.
          </p>
          <Button variant="success" onClick={() => { if (onComplete) onComplete(); }}>
            Finish — create another product
          </Button>
          <div className="mt-3">
            <Button variant="link" style={{ color: '#6c757d', fontSize: 13 }}
              onClick={() => setSaveSuccess(false)}>
              ← Go back and edit ingredients
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="allocate-ingredients-step">

      {/* Skip confirm modal */}
      <Modal show={showSkipConfirm} onHide={() => setShowSkipConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 16 }}>Skip ingredients?</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: 14 }}>
          You can add ingredients later from the product edit page.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={() => setShowSkipConfirm(false)}>Cancel</Button>
          <Button variant="warning" size="sm" onClick={confirmSkip}>Yes, skip</Button>
        </Modal.Footer>
      </Modal>

      <h6 className="mb-1 text-primary fw-semibold">Ingredients Setup</h6>
      <p className="text-muted mb-4" style={{ fontSize: 13 }}>
        This step is optional — you can skip it and add ingredients from the product edit page.
      </p>

      {!productData._id && (
        <Alert variant="warning" className="mb-4">
          <strong>Product not created yet.</strong> Please go back to Step 1 first.
        </Alert>
      )}

      {/* ── Product context ───────────────────────────────────────────── */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <span style={{ fontSize: 13, fontWeight: 600 }}>{productData.name || '—'}</span>
          {existingIngredients && (
            <Badge bg="info" className="ms-2" style={{ fontSize: 10 }}>Editing existing</Badge>
          )}
        </div>
        <Badge bg={totalValid > 0 ? "success" : "secondary"} style={{ fontSize: 11 }}>
          {totalValid} valid ingredient{totalValid !== 1 ? 's' : ''}
        </Badge>
      </div>

      {/* ── FIX A: Compact ingredient list ──────────────────────────── */}
      <Card className="mb-3 border-0 shadow-sm">
        <Card.Body style={{ padding: '0.75rem 1rem' }}>
          {isLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" size="sm" />
              <p className="mt-2 text-muted" style={{ fontSize: 13 }}>Loading ingredients…</p>
            </div>
          ) : (
            <>
              {/* Column headers */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 90px 80px 90px 90px 32px 32px',
                gap: 8, padding: '0 4px 6px',
                borderBottom: '1px solid #e9ecef',
                fontSize: 11, fontWeight: 600, color: '#6c757d'
              }}>
                <span>Raw material *</span>
                <span>Qty *</span>
                <span>Unit</span>
                <span>Important</span>
                <span>Optional</span>
                <span></span>
                <span></span>
              </div>

              {formData.rawMaterials.map((entry, index) => {
                const isExpanded = !!expandedRows[index];
                const selectedAddon = addons.find(a => a._id === entry.addonId);

                return (
                  <div key={index} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    {/* ── Main compact row ── */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 90px 80px 90px 90px 32px 32px',
                      gap: 8, padding: '8px 4px', alignItems: 'center'
                    }}>
                      {/* Raw material select */}
                      <Select
                        options={rawMaterialOptions}
                        value={entry.rawMaterial
                          ? { value: entry.rawMaterial, label: rawMaterialOptions.find(r => r.value === entry.rawMaterial)?.label || '' }
                          : null}
                        onChange={opt => handleRawMaterialChange(index, 'rawMaterial', opt?.value || '')}
                        placeholder="Select material…"
                        classNamePrefix="react-select"
                        isClearable
                        styles={{ control: p => ({ ...p, fontSize: 12, minHeight: 34 }) }}
                      />

                      {/* Quantity */}
                      <Form.Control
                        type="number" step="0.001"
                        value={entry.quantity || ''}
                        onChange={e => handleRawMaterialChange(index, 'quantity', e.target.value)}
                        placeholder="0.00"
                        style={{ fontSize: 12, height: 34 }}
                      />

                      {/* Unit (auto-filled, read-only) */}
                      <Form.Control
                        type="text" value={entry.unit || ''}
                        readOnly placeholder="Auto"
                        style={{ fontSize: 12, height: 34, background: '#f9fafb', color: '#6c757d' }}
                      />

                      {/* Important toggle */}
                      <div className="d-flex justify-content-center">
                        <Form.Check
                          type="checkbox" id={`imp-${index}`}
                          checked={entry.isImportant || false}
                          onChange={e => handleRawMaterialChange(index, 'isImportant', e.target.checked)}
                        />
                      </div>

                      {/* Optional toggle */}
                      <div className="d-flex justify-content-center">
                        <Form.Check
                          type="checkbox" id={`opt-${index}`}
                          checked={entry.isOptional || false}
                          onChange={e => handleRawMaterialChange(index, 'isOptional', e.target.checked)}
                        />
                      </div>

                      {/* FIX A: Expand toggle for addon fields */}
                      <button
                        type="button"
                        title={isExpanded ? 'Hide addon settings' : 'Show addon settings'}
                        onClick={() => toggleRowExpand(index)}
                        style={{
                          border: 'none', background: 'none', cursor: 'pointer',
                          fontSize: 16, color: entry.addonId ? '#0d6efd' : '#9ca3af',
                          padding: 0, lineHeight: 1
                        }}
                      >
                        {isExpanded ? '▲' : '⚙'}
                      </button>

                      {/* Remove row */}
                      {formData.rawMaterials.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeRawMaterialEntry(index)}
                          title="Remove this ingredient"
                          style={{
                            border: 'none', background: 'none', cursor: 'pointer',
                            fontSize: 16, color: '#ef4444', padding: 0, lineHeight: 1
                          }}
                        >✕</button>
                      )}
                    </div>

                    {/* ── FIX A: Expanded addon panel (collapsed by default) ── */}
                    {isExpanded && (
                      <div style={{
                        background: '#f8faff', borderRadius: 6,
                        padding: '12px 12px 16px', margin: '0 4px 8px',
                        border: '1px solid #dbeafe'
                      }}>
                        <p style={{ fontSize: 11, color: '#6c757d', marginBottom: 10 }}>
                          Addon settings — link this ingredient to a specific addon selection
                        </p>
                        <Row className="mb-2">
                          <Col md={6}>
                            <Form.Label style={{ fontSize: 12 }}>Addon group <span className="text-muted">(optional)</span></Form.Label>
                            <Select
                              options={addonOptions}
                              value={entry.addonId ? { value: entry.addonId, label: addonOptions.find(a => a.value === entry.addonId)?.label || '' } : null}
                              onChange={opt => handleRawMaterialChange(index, 'addonId', opt?.value || '')}
                              placeholder="Select addon group…"
                              classNamePrefix="react-select" isClearable
                              styles={{ control: p => ({ ...p, fontSize: 12 }) }}
                            />
                          </Col>
                          <Col md={6}>
                            <Form.Label style={{ fontSize: 12 }}>Addon value <span className="text-muted">(optional)</span></Form.Label>
                            <Select
                              options={getAddonValueOptions(entry.addonId)}
                              value={entry.addonValueId && entry.addonId
                                ? { value: entry.addonValueId, label: getAddonValueOptions(entry.addonId).find(v => v.value === entry.addonValueId)?.label || '' }
                                : null}
                              onChange={opt => handleRawMaterialChange(index, 'addonValueId', opt?.value || '')}
                              placeholder={entry.addonId ? "Select value…" : "Select group first"}
                              classNamePrefix="react-select" isClearable isDisabled={!entry.addonId}
                              styles={{ control: p => ({ ...p, fontSize: 12 }) }}
                            />
                          </Col>
                        </Row>

                        {/* Conditional addons — only when addon group + value are set */}
                        {entry.addonId && entry.addonValueId && (
                          <div style={{ marginTop: 12 }}>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <span style={{ fontSize: 12, fontWeight: 600 }}>Conditional addons</span>
                              <Button variant="outline-primary" size="sm" style={{ fontSize: 11, padding: '2px 8px' }}
                                onClick={() => addConditionalAddon(index)}>
                                + Add condition
                              </Button>
                            </div>
                            {(entry.conditionalAddons || []).length === 0 && (
                              <p style={{ fontSize: 11, color: '#9ca3af' }}>No conditions added.</p>
                            )}
                            {(entry.conditionalAddons || []).map((cond, ci) => {
                              const condAddon = addons.find(a => a._id === cond.addonId);
                              return (
                                <div key={ci} style={{
                                  background: '#fff', border: '1px solid #e5e7eb',
                                  borderRadius: 6, padding: '10px 12px', marginBottom: 8
                                }}>
                                  <div className="d-flex justify-content-between align-items-center mb-2">
                                    <Badge bg="dark" style={{ fontSize: 10 }}>Condition #{ci + 1}</Badge>
                                    <Button variant="outline-danger" size="sm" style={{ fontSize: 11, padding: '1px 6px' }}
                                      onClick={() => removeConditionalAddon(index, ci)}>Remove</Button>
                                  </div>
                                  <Row>
                                    <Col md={4}>
                                      <Form.Label style={{ fontSize: 11 }}>Addon group *</Form.Label>
                                      <Select
                                        options={addonOptions}
                                        value={cond.addonId ? { value: cond.addonId, label: addonOptions.find(a => a.value === cond.addonId)?.label || '' } : null}
                                        onChange={opt => handleConditionalAddonChange(index, ci, 'addonId', opt?.value || '')}
                                        classNamePrefix="react-select" isClearable
                                        styles={{ control: p => ({ ...p, fontSize: 11 }) }}
                                      />
                                    </Col>
                                    <Col md={4}>
                                      <Form.Label style={{ fontSize: 11 }}>Addon value *</Form.Label>
                                      <Select
                                        options={condAddon?.addonValues?.map(v => ({ value: v._id, label: v.value })) || []}
                                        value={cond.addonValueId && cond.addonId
                                          ? { value: cond.addonValueId, label: condAddon?.addonValues?.find(v => v._id === cond.addonValueId)?.value || '' }
                                          : null}
                                        onChange={opt => handleConditionalAddonChange(index, ci, 'addonValueId', opt?.value || '')}
                                        isDisabled={!cond.addonId}
                                        classNamePrefix="react-select" isClearable
                                        styles={{ control: p => ({ ...p, fontSize: 11 }) }}
                                      />
                                    </Col>
                                    <Col md={4}>
                                      <Form.Label style={{ fontSize: 11 }}>Quantity *</Form.Label>
                                      <div className="input-group input-group-sm">
                                        <Form.Control
                                          type="number" step="0.001"
                                          value={cond.quantity}
                                          onChange={e => handleConditionalAddonChange(index, ci, 'quantity', e.target.value)}
                                          placeholder="0.00"
                                          style={{ fontSize: 11 }}
                                        />
                                        <span className="input-group-text" style={{ fontSize: 11 }}>
                                          {entry.unit || '—'}
                                        </span>
                                      </div>
                                    </Col>
                                  </Row>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Add row */}
              <div className="mt-3 text-center">
                <Button variant="outline-primary" size="sm" onClick={addRawMaterialEntry}
                  style={{ fontSize: 13 }}>
                  + Add ingredient
                </Button>
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      {/* ── Tip note (concise, not a wall of text) ───────────────────── */}
      <p style={{ fontSize: 12, color: '#6c757d', marginBottom: '1.25rem' }}>
        <strong>Tips:</strong> Mark an ingredient as <em>Important</em> if it's critical to the recipe.
        Use ⚙ on each row to link it to a specific addon selection.
      </p>

      {/* ─────────────────────────────────────────────────────────────────
          FIX C: Footer button hierarchy
          LEFT:   ← Back                   (outline-secondary)
          CENTER: Skip ingredients          (link/muted)
          RIGHT:  [Save Ingredients]        (success — primary CTA)
                  or [Update Ingredients]   (primary)
      ───────────────────────────────────────────────────────────────── */}
      <div className="mt-2 d-flex justify-content-between align-items-center">

        {/* Left */}
        <Button
          variant="outline-secondary" size="sm"
          onClick={onBack} disabled={isSaving || isSubmitting || isUpdating}
        >
          ← Back
        </Button>

        {/* Centre — de-emphasised skip */}
        <Button
          variant="link" size="sm"
          style={{ color: '#6c757d', textDecoration: 'none', fontSize: 13 }}
          onClick={() => setShowSkipConfirm(true)}
          disabled={isSaving || isSubmitting || isUpdating || !productId}
        >
          Skip ingredients
        </Button>

        {/* Right — primary CTA */}
        <Button
          variant={existingIngredients ? "primary" : "success"}
          size="sm"
          onClick={handleSaveOrUpdate}
          disabled={isSaving || isSubmitting || isUpdating || !productId}
        >
          {isUpdating
            ? <><Spinner size="sm" animation="border" className="me-1" />{existingIngredients ? 'Updating…' : 'Saving…'}</>
            : existingIngredients ? 'Update ingredients' : 'Save ingredients'
          }
        </Button>
      </div>

    </div>
  );
};

export default AllocateIngredients;